var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import Brushaway from '@components/Brushaway';
import TextInput from '@components/TextInput/TextInput';
import InputTextArea, { StyleVariant } from '@components/InputTextArea/InputTextArea';
import { Button } from '@ui';
import { translate } from '@utils/i18n';
import { TRANSLATION_LANGUAGES } from '@constants/translationLanguages';
import { useCreateComponentTypeMutation } from '../hooks/useComponentTypesMutations';
import CategorySelector from './CategorySelector';
import FieldDefinitionsBuilder, { validateFieldDefinitions } from './FieldDefinitionsBuilder';
import EpCodeSelector from './EpCodeSelector';
import DnvTypeSelector from './DnvTypeSelector';
import AttachableTypeSelector from './AttachableTypeSelector';
import './AddComponentTypeModalStyles.scss';
const namespace = 'features.settings.component_types';
export default function AddComponentTypeModal({ isOpen, onClose, availableAttachableTypes, hasJointCommission = false, hasDnvReporting = false, dnvTypeOptions = [], translationLanguages = [] }) {
    const createMutation = useCreateComponentTypeMutation();
    const [allowedAttachableTypes, setAllowedAttachableTypes] = useState([]);
    const [attachableTypesError, setAttachableTypesError] = useState(null);
    const { control, handleSubmit, reset, setValue, watch, formState: { errors, isSubmitting } } = useForm({
        defaultValues: {
            typeNameEn: '',
            typeNameEs: '',
            typeNameFr: '',
            description: '',
            category: '',
            fieldDefinitions: [],
            jointCommissionTitle: '',
            epCode: '',
            ecType: undefined
        }
    });
    const fieldDefinitions = watch('fieldDefinitions');
    const onSubmit = (data) => __awaiter(this, void 0, void 0, function* () {
        // Validate field definitions before submission
        const validationErrors = validateFieldDefinitions(data.fieldDefinitions);
        if (validationErrors.length > 0) {
            return;
        }
        if (allowedAttachableTypes.length === 0) {
            setAttachableTypesError(translate('attachable_types_required', { namespace }));
            return;
        }
        const requestData = {
            typeNameEn: data.typeNameEn,
            typeNameEs: data.typeNameEs || undefined,
            typeNameFr: data.typeNameFr || undefined,
            description: data.description || undefined,
            category: data.category || undefined,
            active: true,
            fieldDefinitions: data.fieldDefinitions.length > 0 ? { fields: data.fieldDefinitions } : undefined,
            allowedAttachableTypes,
            jointCommissionTitle: data.jointCommissionTitle || undefined,
            dnvType: data.dnvType || undefined,
            epCode: data.epCode || undefined,
            ecType: data.ecType || undefined
        };
        try {
            yield createMutation.mutateAsync(requestData);
            handleClose();
        }
        catch (error) {
            // Error handling is done in the mutation hook via toast
            console.error('Failed to create component type:', error);
        }
    });
    const handleClose = () => {
        reset({
            typeNameEn: '',
            typeNameEs: '',
            typeNameFr: '',
            description: '',
            category: '',
            fieldDefinitions: [],
            jointCommissionTitle: '',
            epCode: '',
            ecType: undefined
        });
        setAllowedAttachableTypes([]);
        setAttachableTypesError(null);
        onClose();
    };
    // Wrapper for form submit to handle void return type
    const handleFormSubmit = (e) => {
        void handleSubmit(onSubmit)(e);
    };
    // Wrapper for button click to handle void return type
    const handleButtonSubmit = () => {
        void handleSubmit(onSubmit)();
    };
    const isLoading = isSubmitting || createMutation.isLoading;
    return (React.createElement(Brushaway, { open: isOpen, title: translate('create_new_type', { namespace }), onClose: handleClose, isForm: true, customFooter: React.createElement(React.Fragment, null,
            React.createElement(Button, { onClick: handleClose, disabled: isLoading }, translate('cancel', { namespace: 'features.commons' })),
            React.createElement(Button, { variant: "primary", onClick: handleButtonSubmit, disabled: isLoading }, isLoading ? translate('creating', { namespace }) : translate('create', { namespace: 'features.commons' }))) },
        React.createElement("div", { className: "add-component-type-modal" },
            React.createElement("form", { className: "add-component-type-form", onSubmit: handleFormSubmit },
                React.createElement("div", { className: "form-row" },
                    React.createElement(Controller, { name: "typeNameEn", control: control, rules: { required: translate('type_name_required', { namespace }) }, render: ({ field }) => {
                            var _a;
                            return (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('type_name', { namespace }), error: (_a = errors.typeNameEn) === null || _a === void 0 ? void 0 : _a.message, required: true }));
                        } })),
                translationLanguages.includes(TRANSLATION_LANGUAGES.FR) && (React.createElement("div", { className: "form-row" },
                    React.createElement(Controller, { name: "typeNameFr", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('type_name_fr', { namespace }), placeholder: translate('type_name_fr_placeholder', { namespace }) })) }))),
                translationLanguages.includes(TRANSLATION_LANGUAGES.ES) && (React.createElement("div", { className: "form-row" },
                    React.createElement(Controller, { name: "typeNameEs", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('type_name_es', { namespace }), placeholder: translate('type_name_es_placeholder', { namespace }) })) }))),
                React.createElement("div", { className: "form-row" },
                    React.createElement(Controller, { name: "description", control: control, render: ({ field }) => (React.createElement(InputTextArea
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        , Object.assign({}, field, { label: translate('description', { namespace }), variant: StyleVariant.Light, buttonControlled: false }))) })),
                React.createElement("div", { className: "form-row" },
                    React.createElement(Controller, { name: "category", control: control, rules: { required: translate('category_required', { namespace }) }, render: ({ field }) => {
                            var _a;
                            return (React.createElement(CategorySelector, { value: field.value, onChange: field.onChange, onBlur: field.onBlur, error: (_a = errors.category) === null || _a === void 0 ? void 0 : _a.message, required: true }));
                        } })),
                hasJointCommission && (React.createElement("div", { className: "form-section" },
                    React.createElement("h3", { className: "form-section__title" }, translate('joint_commission_settings', { namespace })),
                    React.createElement("div", { className: "form-row" },
                        React.createElement(Controller, { name: "jointCommissionTitle", control: control, render: ({ field }) => (React.createElement(TextInput, { value: field.value || '', name: field.name, onChange: field.onChange, onBlur: field.onBlur, label: translate('joint_commission_title', { namespace }) })) })),
                    React.createElement(EpCodeSelector, { epCode: watch('epCode'), ecType: watch('ecType'), onChange: (epCode, ecType) => {
                            setValue('epCode', epCode || '');
                            setValue('ecType', ecType || undefined);
                        } }))),
                hasDnvReporting && (React.createElement("div", { className: "form-section" },
                    React.createElement("h3", { className: "form-section__title" }, translate('dnv_settings', { namespace })),
                    React.createElement(DnvTypeSelector, { dnvType: watch('dnvType'), options: dnvTypeOptions, onChange: (dnvType) => setValue('dnvType', dnvType || undefined) }))),
                React.createElement("div", { className: "form-section" },
                    React.createElement("h3", { className: "form-section__title" }, translate('allowed_parent_device_types', { namespace })),
                    React.createElement(AttachableTypeSelector, { selectedTypes: allowedAttachableTypes, availableTypes: availableAttachableTypes, onChange: (types) => {
                            setAllowedAttachableTypes(types);
                            if (types.length > 0)
                                setAttachableTypesError(null);
                        } }),
                    attachableTypesError && React.createElement("p", { className: "qmb-field-error" }, attachableTypesError)),
                React.createElement("div", { className: "form-section" },
                    React.createElement("h3", { className: "form-section__title" }, translate('component_specific_fields', { namespace })),
                    React.createElement(FieldDefinitionsBuilder, { translationLanguages: translationLanguages, fieldDefinitions: fieldDefinitions, onChange: (fields) => setValue('fieldDefinitions', fields) }))))));
}
